/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/contracts.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


export type ContractState = 'draft' | 'pending_signoff' | 'active' | 'retired';

export const CONTRACT_STATES: readonly ContractState[] = ['draft', 'pending_signoff', 'active', 'retired'];

export function isContractState(value: unknown): value is ContractState {
  return (['draft', 'pending_signoff', 'active', 'retired'] as readonly unknown[]).includes(value);
}

export type SignoffSide = 'producer' | 'consumer';

export const SIGNOFF_SIDES: readonly SignoffSide[] = ['producer', 'consumer'];

export function isSignoffSide(value: unknown): value is SignoffSide {
  return (['producer', 'consumer'] as readonly unknown[]).includes(value);
}

/**
 * At least one term must be non-null. Every term treats the absence of its fact as a breach — an
 * unmeasured promise is not a kept one. All three keys are always serialized, null when not
 * promised: absent-vs-null is a guess a client should not have to make.
 */
export interface ContractTerms {
  readonly min_quality_score: number | null;
  readonly max_assessment_age_seconds: number | null;
  /**
   * The catalog schema version the consumer signed against. ANY change is a breach, forward
   * included — the consumer's parsers were written against this version.
   */
  readonly pinned_schema_version: number | null;
}

export function isContractTerms(value: unknown): value is ContractTerms {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate['min_quality_score'] === 'number' || candidate['min_quality_score'] === null) &&
    (typeof candidate['max_assessment_age_seconds'] === 'number' || candidate['max_assessment_age_seconds'] === null) &&
    (typeof candidate['pinned_schema_version'] === 'number' || candidate['pinned_schema_version'] === null)
  );
}

/**
 * What the terms were measured against. **A breach says a promise was not kept and does not say by
 * how much.** "Minimum score 80, observed 62" is a conversation with the producer; "score term
 * breached" is a ticket somebody has to go and investigate. This is returned so a reader can see
 * the gap rather than infer it. **Every field is nullable and the nulls are the answer**, not a
 * default: a dataset never scored reports `latest_score: null` rather than 0, and that is
 * precisely why a score term breaches — an unmeasured promise is not a kept one. Rendering either
 * as a zero would report an unexamined dataset as a failing one. Scores, instants and version
 * numbers only — metadata about a dataset, never a value from it.
 */
export interface ObservedFacts {
  readonly latest_score: number | null;
  readonly last_assessed_at: string | null;
  readonly current_schema_version: number | null;
}

export function isObservedFacts(value: unknown): value is ObservedFacts {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate['latest_score'] === 'number' || candidate['latest_score'] === null) &&
    (typeof candidate['last_assessed_at'] === 'string' || candidate['last_assessed_at'] === null) &&
    (typeof candidate['current_schema_version'] === 'number' || candidate['current_schema_version'] === null)
  );
}

export interface BreachFinding {
  readonly term: string;
  /** Stable, machine-readable-ish — term plus expectation. */
  readonly cause: string;
  /**
   * The sentence a human reads in the incident queue. Counts and versions only, never a value from
   * the customer's table.
   */
  readonly summary: string;
}

export function isBreachFinding(value: unknown): value is BreachFinding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['term'] === 'string' &&
    typeof candidate['cause'] === 'string' &&
    typeof candidate['summary'] === 'string'
  );
}

export interface Contract {
  readonly id: string;
  readonly dataset_id: string;
  readonly name: string;
  readonly state: ContractState;
  readonly producer_subject: string;
  readonly consumer_subject: string;
  readonly breach_severity: 'info' | 'warning' | 'critical';
  readonly created_by: string;
  readonly created_at: string;
  readonly terms: ContractTerms;
  readonly producer_signed_by: string | null;
  readonly producer_signed_at: string | null;
  readonly consumer_signed_by: string | null;
  readonly consumer_signed_at: string | null;
  /** Always present; empty means both parties have signed. */
  readonly unsigned_sides: readonly SignoffSide[];
}

export function isContract(value: unknown): value is Contract {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    (['draft', 'pending_signoff', 'active', 'retired'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['producer_subject'] === 'string' &&
    typeof candidate['consumer_subject'] === 'string' &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['breach_severity']) &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    candidate['terms'] !== undefined &&
    (typeof candidate['producer_signed_by'] === 'string' || candidate['producer_signed_by'] === null) &&
    (typeof candidate['producer_signed_at'] === 'string' || candidate['producer_signed_at'] === null) &&
    (typeof candidate['consumer_signed_by'] === 'string' || candidate['consumer_signed_by'] === null) &&
    (typeof candidate['consumer_signed_at'] === 'string' || candidate['consumer_signed_at'] === null) &&
    Array.isArray(candidate['unsigned_sides']) &&
    (candidate['unsigned_sides'] as readonly unknown[]).every((item) => isSignoffSide(item))
  );
}

export interface ContractWrite {
  readonly dataset_id: string;
  readonly name: string;
  /** The token `sub` of the publishing party. Must differ from the consumer. */
  readonly producer_subject: string;
  readonly consumer_subject: string;
  readonly terms: ContractTerms;
  /**
   * How loud a breach incident is. Defaults to warning, not critical: defaulting to the loudest
   * teaches people to ignore it.
   */
  readonly breach_severity?: 'info' | 'warning' | 'critical';
}

export function isContractWrite(value: unknown): value is ContractWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['producer_subject'] === 'string' &&
    typeof candidate['consumer_subject'] === 'string' &&
    candidate['terms'] !== undefined
  );
}

export interface EvaluationOutcome {
  readonly contract_id: string;
  readonly contract_name: string;
  readonly held: boolean;
  readonly breaches: readonly BreachFinding[];
  readonly observed: ObservedFacts;
  /**
   * False beside breaches means one is already open for this contract — the ongoing-breach case,
   * deduplicated at the substrate.
   */
  readonly incident_opened: boolean;
}

export function isEvaluationOutcome(value: unknown): value is EvaluationOutcome {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['contract_id'] === 'string' &&
    typeof candidate['contract_name'] === 'string' &&
    typeof candidate['held'] === 'boolean' &&
    Array.isArray(candidate['breaches']) &&
    (candidate['breaches'] as readonly unknown[]).every((item) => isBreachFinding(item)) &&
    typeof candidate['incident_opened'] === 'boolean' &&
    candidate['observed'] !== undefined
  );
}
